import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CheckCircle2, Send, Lock, Loader2, AlertTriangle } from 'lucide-react';
import { useRosterSubmission } from '@/hooks/useRosterSubmission';
import { format } from 'date-fns';

interface RosterSubmitButtonProps {
  eventId: string;
  teamId: string;
  teamName: string;
  eventName: string;
  playerCount: number;
  rosterDeadline?: string;
  onSubmitted?: () => void;
}

const RosterSubmitButton: React.FC<RosterSubmitButtonProps> = ({
  eventId,
  teamId,
  teamName,
  eventName,
  playerCount,
  rosterDeadline,
  onSubmitted
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lockAfterSubmit, setLockAfterSubmit] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<{
    isSubmitted: boolean;
    submittedAt?: string;
    isLocked: boolean;
  }>({ isSubmitted: false, isLocked: false });

  const { isSubmitting, submitRoster, getRosterSubmissionStatus } = useRosterSubmission();

  useEffect(() => {
    if (eventId && teamId) {
      checkSubmissionStatus();
    }
  }, [eventId, teamId]);

  const checkSubmissionStatus = async () => {
    const status = await getRosterSubmissionStatus(eventId, teamId);
    setSubmissionStatus(status);
  };

  const handleSubmit = async () => {
    const result = await submitRoster(eventId, teamId, teamName, eventName, lockAfterSubmit);
    
    if (result.success) {
      setSubmissionStatus({
        isSubmitted: true,
        submittedAt: result.submittedAt,
        isLocked: lockAfterSubmit
      });
      setShowConfirmDialog(false);
      onSubmitted?.();
    }
  };

  const isDeadlinePassed = rosterDeadline && new Date(rosterDeadline) < new Date();

  // Confirmation Dialog - always rendered so it works for both submit and resubmit
  const confirmDialog = (
    <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Submit Roster for {eventName}</DialogTitle>
          <DialogDescription>
            You are about to submit the roster for <strong>{teamName}</strong> with{' '}
            <strong>{playerCount} player{playerCount !== 1 ? 's' : ''}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="lock-roster"
              checked={lockAfterSubmit}
              onCheckedChange={(checked) => setLockAfterSubmit(checked as boolean)}
            />
            <Label htmlFor="lock-roster" className="text-sm">
              Lock roster after submission (prevents further changes)
            </Label>
          </div>

          <Alert>
            <AlertDescription>
              The event administrators will be notified of your roster submission.
              {!lockAfterSubmit && ' You can still make changes and resubmit until the deadline.'}
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Confirm Submission
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Already submitted
  if (submissionStatus.isSubmitted) {
    return (
      <>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-green-800">Roster Submitted</span>
                  {submissionStatus.isLocked && (
                    <Badge variant="outline" className="text-amber-700 border-amber-300">
                      <Lock className="h-3 w-3 mr-1" />
                      Locked
                    </Badge>
                  )}
                </div>
                {submissionStatus.submittedAt && (
                  <p className="text-sm text-green-700">
                    Submitted on {format(new Date(submissionStatus.submittedAt), 'MMM d, yyyy \'at\' h:mm a')}
                  </p>
                )}
                <p className="text-sm text-green-700">{playerCount} players on roster</p>
              </div>
              {!submissionStatus.isLocked && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowConfirmDialog(true)}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  Resubmit
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <Card className="border-primary-200 bg-primary-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-primary-800">Ready to Submit Your Roster?</h3>
              <p className="text-sm text-primary-700">
                {playerCount} player{playerCount !== 1 ? 's' : ''} on roster
                {rosterDeadline && (
                  <span className={isDeadlinePassed ? 'text-red-600 font-medium' : ''}>
                    {' '}• Deadline: {format(new Date(rosterDeadline), 'MMM d, yyyy')}
                    {isDeadlinePassed && ' (Passed)'}
                  </span>
                )}
              </p>
            </div>
            <Button 
              onClick={() => setShowConfirmDialog(true)}
              disabled={playerCount === 0}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              Submit Roster
            </Button>
          </div>
          {playerCount === 0 && (
            <Alert className="mt-3 border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Add at least one player before submitting your roster.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
      {confirmDialog}
    </>
  );
};

export default RosterSubmitButton;
